/**
 * Custom studies: what is running, what it is doing, and where it came from.
 *
 * ## Provenance is on the row, not behind a menu
 *
 * A trader installs code a stranger wrote. Every row therefore says whose it
 * is, and a row for somebody else's script that has been EDITED since it was
 * installed refuses to run until they look again — that refusal is the loudest
 * thing on the panel, because an author who could silently swap the body of an
 * installed script would have a live update channel into other people's
 * applications.
 *
 * ## The source is always readable
 *
 * Both here and in the install preview. A study that runs code the trader
 * cannot see is not reviewable, and reviewable is the entire premise on which
 * running somebody else's code is defensible at all.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import {
  MAX_SCRIPT_NAME, SCRIPT_PLOT_STYLES, SCRIPT_SOURCES,
  lintScriptSource, resolveScriptInputs,
  type ScriptInputDecl, type ScriptPlotDecl,
} from '@aegis/core';
import type { InstalledScriptDto, MyScriptDto, SharedScriptDto } from '@aegis/api-client';
import { api } from '../lib/api.js';
import { useScripts, type ScriptRunState } from '../lib/scripts.js';
import { SCRIPT_PALETTE } from '../lib/studies.js';

/** A starting point that runs, so a new author sees a line before they read anything. */
const STARTER = `// Bars arrive as arrays: time, open, high, low, close, volume.
// Return one value per bar for each plot you declare, or null for none.
const length = inputs.length;
const out = [];
let sum = 0;
for (let i = 0; i < close.length; i += 1) {
  sum += close[i];
  if (i >= length) sum -= close[i - length];
  out.push(i < length - 1 ? null : sum / length);
}
return { ma: out };
`;

type Draft = {
  id?: string;
  name: string;
  overlay: boolean;
  source: string;
  inputs: ScriptInputDecl[];
  plots: ScriptPlotDecl[];
};

const NEW_DRAFT: Draft = {
  name: 'My average',
  overlay: true,
  source: STARTER,
  inputs: [{ id: 'length', label: 'Length', kind: 'integer', def: 20, min: 1, max: 500 }],
  plots: [{ id: 'ma', label: 'MA', style: 'line', colorIndex: 0, lineWidth: 2 }],
};

export function StudiesPanel(
  { onError }: { onError?: (message: string) => void },
): JSX.Element {
  const { scripts, loading, reload, runStates } = useScripts();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<SharedScriptDto | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (e: unknown): void => {
    onError?.(e instanceof Error ? e.message : 'That did not work.');
  };

  const save = async (): Promise<void> => {
    if (!draft) return;
    setBusy(true);
    setProblems([]);
    try {
      await api.saveScript({
        ...(draft.id ? { id: draft.id } : {}),
        name: draft.name,
        source: draft.source,
        overlay: draft.overlay,
        inputs: draft.inputs,
        plots: draft.plots,
      });
      setDraft(null);
      reload();
    } catch (e) {
      /* The server's own validation, shown against the form rather than as a
         toast. These are the author's mistakes and they need to stay on screen
         while they fix them. */
      const problems = (e as { body?: { problems?: string[] } }).body?.problems;
      if (problems?.length) setProblems(problems);
      else fail(e);
    } finally {
      setBusy(false);
    }
  };

  if (draft) {
    return (
      <Editor
        draft={draft}
        problems={problems}
        busy={busy}
        onChange={setDraft}
        onCancel={() => { setDraft(null); setProblems([]); }}
        onSave={() => void save()}
      />
    );
  }

  if (preview) {
    return (
      <Preview
        preview={preview}
        onCancel={() => { setPreview(null); setCode(''); }}
        onInstall={() => {
          setBusy(true);
          void api.installScript(code)
            .then(() => { setPreview(null); setCode(''); reload(); })
            .catch(fail)
            .finally(() => setBusy(false));
        }}
        busy={busy}
      />
    );
  }

  const installed = scripts?.installed ?? [];

  return (
    <aside className="panel studies-panel">
      <header className="panel-head">
        <h2 className="panel-title">Studies</h2>
        <button type="button" className="chip-btn compact" onClick={() => setDraft(NEW_DRAFT)}>
          New
        </button>
      </header>

      <form
        className="study-install"
        onSubmit={(e) => {
          e.preventDefault();
          if (!code.trim()) return;
          setBusy(true);
          void api.sharedScript(code.trim())
            .then((p) => { setPreview(p); })
            .catch(fail)
            .finally(() => setBusy(false));
        }}
      >
        <input
          type="text"
          className="study-code"
          placeholder="Paste a share code"
          value={code}
          aria-label="Share code"
          onChange={(e) => setCode(e.target.value)}
        />
        <button type="submit" className="chip-btn compact" disabled={busy || !code.trim()}>
          Look
        </button>
      </form>

      {loading && installed.length === 0 && <p className="panel-empty">Loading…</p>}
      {!loading && installed.length === 0 && (
        <p className="panel-empty">
          No studies yet. Write one, or paste a share code somebody sent you.
        </p>
      )}

      <ul className="study-list">
        {installed.map((s) => (
          <StudyRow
            key={s.id}
            script={s}
            run={runStates[s.id]}
            onEdit={() => setDraft({
              id: s.id,
              name: s.name,
              overlay: s.overlay,
              source: s.source,
              inputs: s.inputs as ScriptInputDecl[],
              plots: s.plots as ScriptPlotDecl[],
            })}
            onChanged={reload}
            onError={fail}
            mineList={scripts?.mine ?? []}
          />
        ))}
      </ul>
    </aside>
  );
}

function StudyRow({
  script, run, onEdit, onChanged, onError, mineList,
}: {
  script: InstalledScriptDto;
  run: ScriptRunState | undefined;
  onEdit: () => void;
  onChanged: () => void;
  onError: (e: unknown) => void;
  mineList: readonly MyScriptDto[];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const authored = mineList.find((m) => m.id === script.id);
  const values = resolveScriptInputs(
    script.inputs as readonly ScriptInputDecl[],
    script.settings as Record<string, unknown>,
  );

  const patch = (body: { settings?: Record<string, unknown>; enabled?: boolean }): void => {
    void api.setScriptSettings(script.id, body).then(onChanged).catch(onError);
  };

  return (
    <li className={`study-row ${script.enabled ? 'on' : ''}`}>
      <div className="study-head">
        <label className="study-toggle">
          <input
            type="checkbox"
            checked={script.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          <span className="study-name">{script.name}</span>
        </label>
        <div className="study-actions">
          <button type="button" className="chip-btn compact" onClick={() => setOpen(!open)}>
            {open ? 'Less' : 'More'}
          </button>
          {script.mine && (
            <button type="button" className="chip-btn compact" onClick={onEdit}>Edit</button>
          )}
        </div>
      </div>

      <p className="study-origin">
        {script.mine ? 'Yours' : 'Written by someone else'}
        {' · '}
        <span className="study-hash" title={script.fingerprint}>
          {script.fingerprint.slice(0, 12)}
        </span>
      </p>

      {script.status === 'removed' && (
        <p className="study-alert">
          This script was taken down and no longer runs.
          {script.removedReason ? ` ${script.removedReason}` : ''}
        </p>
      )}

      {script.changed && script.status !== 'removed' && (
        <p className="study-alert">
          {script.mine
            ? 'Edited elsewhere since this device last saw it. Reopen it to pick up the change.'
            : 'The author has changed this code since you installed it, so it is not running. '
              + 'Read it again and install it once more to keep using it.'}
        </p>
      )}

      {run && run.state !== 'ok' && run.state !== 'running' && (
        <p className="study-alert">{run.message ?? 'This study did not run.'}</p>
      )}
      {run?.state === 'ok' && run.gaps > 0 && (
        <p className="study-note">
          {run.gaps} bar{run.gaps === 1 ? '' : 's'} produced no value. Warm-up is the usual reason.
        </p>
      )}

      {open && (
        <div className="study-detail">
          {(script.inputs as readonly ScriptInputDecl[]).map((input) => (
            <InputField
              key={input.id}
              decl={input}
              value={values[input.id]!}
              onChange={(v) => patch({ settings: { ...values, [input.id]: v } })}
            />
          ))}

          <p className="study-label">Plots</p>
          <ul className="study-plots">
            {(script.plots as readonly ScriptPlotDecl[]).map((p) => (
              <li key={p.id}>
                <span
                  className="study-swatch"
                  style={{ background: `var(--${kebab(SCRIPT_PALETTE[p.colorIndex] ?? 'accent')})` }}
                />
                {p.label} · {p.style}
              </li>
            ))}
          </ul>

          <p className="study-label">Source</p>
          <pre className="study-source">{script.source}</pre>

          {authored ? (
            <ShareControls script={authored} onChanged={onChanged} onError={onError} />
          ) : (
            <ReportControls script={script} onChanged={onChanged} onError={onError} />
          )}
        </div>
      )}
    </li>
  );
}

function ShareControls({
  script, onChanged, onError,
}: {
  script: MyScriptDto;
  onChanged: () => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (script.status === 'removed') {
    return <p className="study-note">Taken down. It cannot be shared or edited.</p>;
  }

  return (
    <div className="study-share">
      {script.shareCode ? (
        <>
          <p className="study-note">
            Anyone with this code can read and install this script.
          </p>
          <div className="study-share-row">
            <code className="study-code-out">{script.shareCode}</code>
            <button
              type="button"
              className="chip-btn compact"
              onClick={() => {
                void navigator.clipboard?.writeText(script.shareCode ?? '')
                  .then(() => setCopied(true))
                  .catch(() => { /* No clipboard permission; the code is on screen. */ });
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              className="chip-btn compact"
              onClick={() => { void api.unshareScript(script.id).then(onChanged).catch(onError); }}
            >
              Withdraw
            </button>
          </div>
          <p className="study-note">
            Withdrawing drops the code. Anyone who wrote it down has nothing, and
            people who already installed it keep what they have.
          </p>
        </>
      ) : (
        <button
          type="button"
          className="chip-btn compact"
          onClick={() => { void api.shareScript(script.id).then(onChanged).catch(onError); }}
        >
          Share
        </button>
      )}
      <button
        type="button"
        className="chip-btn compact danger"
        onClick={() => { void api.deleteScript(script.id).then(onChanged).catch(onError); }}
      >
        Delete
      </button>
    </div>
  );
}

function ReportControls({
  script, onChanged, onError,
}: {
  script: InstalledScriptDto;
  onChanged: () => void;
  onError: (e: unknown) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState(false);

  return (
    <div className="study-share">
      <button
        type="button"
        className="chip-btn compact"
        onClick={() => { void api.uninstallScript(script.id).then(onChanged).catch(onError); }}
      >
        Remove
      </button>
      {sent ? (
        <p className="study-note">Reported. A person reads these; nothing happens automatically.</p>
      ) : (
        <form
          className="study-report"
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason.trim()) return;
            void api.reportScript(script.id, reason.trim())
              .then(() => setSent(true))
              .catch(onError);
          }}
        >
          <input
            type="text"
            value={reason}
            aria-label="Why you are reporting this script"
            placeholder="Report it — say what is wrong"
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="submit" className="chip-btn compact" disabled={!reason.trim()}>
            Send
          </button>
        </form>
      )}
    </div>
  );
}

function InputField({
  decl, value, onChange,
}: {
  decl: ScriptInputDecl;
  value: number | boolean | string;
  onChange: (v: number | boolean | string) => void;
}): JSX.Element {
  if (decl.kind === 'boolean') {
    return (
      <label className="study-field">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        {decl.label}
      </label>
    );
  }

  if (decl.kind === 'source' || decl.kind === 'select') {
    return (
      <label className="study-field">
        <span>{decl.label}</span>
        <select value={String(value)} onChange={(e) => onChange(e.target.value)}>
          {(decl.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }

  return (
    <label className="study-field">
      <span>{decl.label}</span>
      <input
        type="number"
        value={String(value)}
        {...(decl.min !== undefined ? { min: decl.min } : {})}
        {...(decl.max !== undefined ? { max: decl.max } : {})}
        step={decl.kind === 'integer' ? 1 : 'any'}
        onChange={(e) => {
          const n = Number(e.target.value);
          /* The clamp lives in `resolveScriptInputs` and runs again before
             every run. This only avoids storing a NaN, which would be shown
             back to the trader as an empty box. */
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </label>
  );
}

function Preview({
  preview, onInstall, onCancel, busy,
}: {
  preview: SharedScriptDto;
  onInstall: () => void;
  onCancel: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <aside className="panel studies-panel">
      <header className="panel-head">
        <h2 className="panel-title">{preview.name}</h2>
        <button type="button" className="chip-btn compact" onClick={onCancel}>Back</button>
      </header>
      <p className="study-alert">
        Somebody else wrote this. It runs in a sandbox with no network and no access
        to your journal or your account — but it can still be wrong about the market.
        Read it before you install it.
      </p>
      {preview.notes.length > 0 && (
        <ul className="study-notes">
          {preview.notes.map((n) => <li key={n.name}>{n.message}</li>)}
        </ul>
      )}
      <p className="study-label">Source</p>
      <pre className="study-source">{preview.source}</pre>
      <p className="study-note">
        <span className="study-hash">{preview.fingerprint.slice(0, 12)}</span>
        {' — the code above, exactly. If the author changes it, your study stops '}
        until you read it again.
      </p>
      <button type="button" className="chip-btn" onClick={onInstall} disabled={busy}>
        {preview.alreadyInstalled ? 'Install this version' : 'Install'}
      </button>
    </aside>
  );
}

function Editor({
  draft, problems, busy, onChange, onSave, onCancel,
}: {
  draft: Draft;
  problems: readonly string[];
  busy: boolean;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element {
  const notes = lintScriptSource(draft.source);

  return (
    <aside className="panel studies-panel">
      <header className="panel-head">
        <h2 className="panel-title">{draft.id ? 'Edit study' : 'New study'}</h2>
        <button type="button" className="chip-btn compact" onClick={onCancel}>Cancel</button>
      </header>

      <label className="study-field">
        <span>Name</span>
        <input
          type="text"
          value={draft.name}
          maxLength={MAX_SCRIPT_NAME}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </label>

      <label className="study-field">
        <input
          type="checkbox"
          checked={draft.overlay}
          onChange={(e) => onChange({ ...draft, overlay: e.target.checked })}
        />
        Draw on the price pane
      </label>

      <p className="study-label">Plots</p>
      {draft.plots.map((plot, i) => (
        <div className="study-plot-edit" key={plot.id + String(i)}>
          <input
            type="text"
            aria-label="Plot id"
            value={plot.id}
            onChange={(e) => onChange({
              ...draft,
              plots: draft.plots.map((p, j) => (j === i ? { ...p, id: e.target.value } : p)),
            })}
          />
          <select
            aria-label="Plot style"
            value={plot.style}
            onChange={(e) => onChange({
              ...draft,
              plots: draft.plots.map((p, j) => (
                j === i ? { ...p, style: e.target.value as ScriptPlotDecl['style'] } : p)),
            })}
          >
            {SCRIPT_PLOT_STYLES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          <select
            aria-label="Plot colour"
            value={plot.colorIndex}
            onChange={(e) => onChange({
              ...draft,
              plots: draft.plots.map((p, j) => (
                j === i ? { ...p, colorIndex: Number(e.target.value) } : p)),
            })}
          >
            {SCRIPT_PALETTE.map((tokenName, index) => (
              <option key={tokenName} value={index}>{index + 1}</option>
            ))}
          </select>
          <button
            type="button"
            className="chip-btn compact"
            aria-label={`Remove plot ${plot.id}`}
            disabled={draft.plots.length === 1}
            onClick={() => onChange({
              ...draft, plots: draft.plots.filter((_, j) => j !== i),
            })}
          >
            −
          </button>
        </div>
      ))}
      <button
        type="button"
        className="chip-btn compact"
        onClick={() => onChange({
          ...draft,
          plots: [...draft.plots, {
            id: `plot${draft.plots.length + 1}`,
            label: `Plot ${draft.plots.length + 1}`,
            style: 'line',
            colorIndex: draft.plots.length % SCRIPT_PALETTE.length,
            lineWidth: 1,
          }],
        })}
      >
        Add a plot
      </button>

      <p className="study-label">Inputs</p>
      {draft.inputs.map((input, i) => (
        <div className="study-plot-edit" key={input.id + String(i)}>
          <input
            type="text"
            aria-label="Input id"
            value={input.id}
            onChange={(e) => onChange({
              ...draft,
              inputs: draft.inputs.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)),
            })}
          />
          <select
            aria-label="Input kind"
            value={input.kind}
            onChange={(e) => {
              const kind = e.target.value as ScriptInputDecl['kind'];
              /* The default has to move with the kind — a numeric default on a
                 source input is refused, and a form that let the two disagree
                 would fail on save with a message about a field the author
                 never touched. */
              const def = kind === 'boolean' ? true
                : kind === 'source' ? 'close'
                  : kind === 'select' ? 'a' : 20;
              onChange({
                ...draft,
                inputs: draft.inputs.map((x, j) => (j === i
                  ? {
                    ...x, kind, def,
                    ...(kind === 'select' ? { options: ['a', 'b'] } : {}),
                    ...(kind === 'source' ? { options: [...SCRIPT_SOURCES] } : {}),
                  }
                  : x)),
              });
            }}
          >
            {['integer', 'number', 'boolean', 'source', 'select'].map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          <button
            type="button"
            className="chip-btn compact"
            aria-label={`Remove input ${input.id}`}
            onClick={() => onChange({
              ...draft, inputs: draft.inputs.filter((_, j) => j !== i),
            })}
          >
            −
          </button>
        </div>
      ))}
      <button
        type="button"
        className="chip-btn compact"
        onClick={() => onChange({
          ...draft,
          inputs: [...draft.inputs, {
            id: `input${draft.inputs.length + 1}`,
            label: `Input ${draft.inputs.length + 1}`,
            kind: 'integer', def: 20, min: 1, max: 500,
          }],
        })}
      >
        Add an input
      </button>

      <p className="study-label">Source</p>
      <textarea
        className="study-editor"
        aria-label="Script source"
        spellCheck={false}
        value={draft.source}
        onChange={(e) => onChange({ ...draft, source: e.target.value })}
      />

      {notes.length > 0 && (
        <ul className="study-notes">
          {notes.map((n) => <li key={n.name}>{n.message}</li>)}
        </ul>
      )}
      {problems.length > 0 && (
        <ul className="study-notes bad">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <button type="button" className="chip-btn" onClick={onSave} disabled={busy}>
        Save
      </button>
    </aside>
  );
}

/** `statusCaution` to `status-caution`, to reach the CSS custom property. */
function kebab(name: string): string {
  return name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
